import React from 'react';
import { styled, SxProp, useSx } from 'dripsy';
import { Animated } from 'react-native';
import { observer } from 'mobx-react-lite';
import { useCollapsibleScene } from 'react-native-collapsible-tab-view';

import { TOP_TAB_BAR_HEIGHT } from '@navigation';
import { ScrollView } from '../common/ScrollView';
import { ScreenLoader } from '../common/ScreenLoader';
import { RefreshControl } from '../common/RefreshControl';
import { EmptyResultView } from './EmptyResultView';
import { SCREEN_TOP_BAR_HEIGHT } from './ScreenTopBar';

const contentContainerStyle: SxProp = {
  pt: SCREEN_TOP_BAR_HEIGHT + TOP_TAB_BAR_HEIGHT,
};

const emptyContentContainerStyle: SxProp = {
  ...contentContainerStyle,
  flexGrow: 1,
  alignItems: 'center',
  justifyContent: 'center',
};

const EmptyScrollViewContainer = styled(ScrollView)({
  flex: 1,
});

interface IHorizontalTabViewProps {
  routeName: string;
  isLoading: boolean;
  isError: boolean;
  isRefreshing: boolean;
  onRefresh(): void;
  children: React.ReactNode;
}

export const HorizontalTabView = observer<IHorizontalTabViewProps>(
  ({
    routeName,
    isError,
    isLoading,
    isRefreshing,
    onRefresh,
    children,
  }) => {
    const sx = useSx();
    const sceneProps = useCollapsibleScene(routeName);

    if (isLoading) {
      return <ScreenLoader />;
    }

    if (isError) {
      return (
        <EmptyScrollViewContainer
          contentContainerSx={emptyContentContainerStyle}
          refreshControl={
            <RefreshControl
              onRefresh={onRefresh}
              refreshing={isRefreshing}
            />
          }
        >
          <EmptyResultView />
        </EmptyScrollViewContainer>
      );
    }

    return (
      <Animated.ScrollView
        {...sceneProps}
        contentContainerStyle={[
          sx(contentContainerStyle),
          sceneProps.contentContainerStyle,
        ]}
        refreshControl={
          <RefreshControl
            onRefresh={onRefresh}
            refreshing={isRefreshing}
          />
        }
      >
        {children}
      </Animated.ScrollView>
    );
  },
);
